import React, { useEffect, useState, useCallback } from "react";
import { DeviceMobile, Share, Plus, X, CheckCircle } from "@phosphor-icons/react";
import {
    hasDeferredInstall, isStandalone, isIOS, subscribe, triggerInstall,
} from "../lib/installPrompt";

/**
 * InstallAppLink — donor-facing "📲 Install app" link non-profits can include
 * in their storefront footer, email blasts, and event invites.
 *
 *  - Hidden when the app is already installed (display-mode: standalone).
 *  - On Chromium with a deferred `beforeinstallprompt`, click fires the
 *    native install dialog directly.
 *  - On iOS Safari (or any browser without the deferred event), click opens
 *    a tiny modal explaining the Share → Add-to-Home-Screen flow.
 *
 * Props:
 *  - orgName?: string  — used in the iOS instructions (defaults to "CharityFundraiser")
 *  - className?: string
 *  - variant?: "link" | "button"  — visual style (default "link")
 */
export default function InstallAppLink({ orgName = "", className = "", variant = "link" }) {
    const [hasDeferred, setHasDeferred] = useState(hasDeferredInstall());
    const [helpOpen, setHelpOpen] = useState(false);
    const [installedFlash, setInstalledFlash] = useState(false);

    useEffect(() => subscribe(() => setHasDeferred(hasDeferredInstall())), []);

    const onClick = useCallback(async (e) => {
        e.preventDefault();
        const res = await triggerInstall();
        if (res === "installed") {
            setInstalledFlash(true);
            setTimeout(() => setInstalledFlash(false), 4000);
            return;
        }
        if (res === "unavailable") {
            // No native dialog (iOS, Firefox, or already dismissed) — open help.
            setHelpOpen(true);
        }
    }, []);

    if (isStandalone()) return null;

    const label = orgName ? `Install the ${orgName} app` : "Install app";

    const linkCls = variant === "button"
        ? "inline-flex items-center gap-2 bg-accent text-accent-foreground px-4 py-2 text-xs font-bold uppercase tracking-wider hover:opacity-90"
        : "inline-flex items-center gap-1.5 text-sm text-primary-foreground/85 hover:text-accent transition-colors underline-offset-4 hover:underline";

    return (
        <>
            <button
                type="button"
                onClick={onClick}
                className={`${linkCls} ${className}`.trim()}
                data-testid="install-app-link"
                aria-haspopup={hasDeferred ? "false" : "dialog"}
            >
                <DeviceMobile size={14} weight="fill" />
                {installedFlash ? (
                    <span className="inline-flex items-center gap-1"><CheckCircle size={14} weight="fill"/> Installed!</span>
                ) : label}
            </button>

            {helpOpen && (
                <div
                    className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
                    role="dialog"
                    aria-modal="true"
                    data-testid="install-help-modal"
                    onClick={() => setHelpOpen(false)}
                >
                    <div
                        className="bg-white border-2 border-accent max-w-sm w-full p-5 relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setHelpOpen(false)}
                            aria-label="Close"
                            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
                            data-testid="install-help-close"
                        >
                            <X size={16} />
                        </button>
                        <p className="label-eyebrow text-[10px] text-accent">Install the app</p>
                        {isIOS() ? (
                            <>
                                <p className="font-heading text-lg text-primary mt-1 leading-snug">
                                    {orgName ? `Add the ${orgName} app to your Home Screen` : "Add to your Home Screen"}
                                </p>
                                <ol className="text-sm text-foreground mt-3 space-y-2 list-decimal pl-5">
                                    <li>
                                        Tap the <Share size={14} className="inline -mt-0.5" weight="fill" /> share button in Safari's toolbar.
                                    </li>
                                    <li>
                                        Choose <span className="font-semibold">Add to Home Screen</span>{" "}
                                        <Plus size={12} className="inline -mt-0.5" />.
                                    </li>
                                    <li>
                                        Tap <span className="font-semibold">Add</span> in the top right.
                                    </li>
                                </ol>
                                <p className="text-xs text-muted-foreground mt-3">
                                    Opens full-screen with no browser bar. Bids, donations, and event tickets all in one tap.
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="font-heading text-lg text-primary mt-1 leading-snug">
                                    Add to your home screen
                                </p>
                                <p className="text-sm text-foreground mt-2">
                                    In your browser menu, choose <span className="font-semibold">Install app</span>{" "}
                                    (Chrome / Edge) or <span className="font-semibold">Add to Home Screen</span>{" "}
                                    (Firefox / Samsung Internet).
                                </p>
                                <p className="text-xs text-muted-foreground mt-3">
                                    Once installed you'll get a dedicated icon on your home screen — no app store needed.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
